import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form as FormikForm, FormikProps } from 'formik';
import { InferType, object, string, number } from 'yup';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import { FormGroupControl, FormGroupTypeahead } from '@components/Base';
import { ServerError } from '@components/Helper';
import i18n from '@components/i18n';
import { HealthClaim } from '@models/HealthClaim';
import { DiagnosisCode } from '@models/DiagnosisCode';
import { BusinessPartner } from '@models/BusinessPartner';
import { mergeInitialValues } from '@utils/formik';
import { DATETIME_FORMAT, formatDateTime } from '@utils/datetime';

import { healthClaimUpdate, HealthClaimUpdatePayload, diagnosisCodeSearch } from './action';
import { selectHealthClaim } from './selector';

export interface HealthClaimDetailsEditModalProps {
  isOpen: boolean;
  healthClaim: HealthClaim;
  onClose: () => void;
  onSuccess?: () => void;
}

const groupProps = { className: 'mb-3' };

const validationSchema = object({
  accidentHappenedAt: string().default('').required(i18n.t('validation:required')),
  dischargedAt: string().default('').required(i18n.t('validation:required')),
  hospitalLengthOfStay: number().default(0).required(i18n.t('validation:required')),
  symptomsDescription: string().default('').required(i18n.t('validation:required')),
  diagnosisDescription: string().default('').required(i18n.t('validation:required')),
  businessPartnerId: string()
    .default('')
    .when('hospitalName', {
      is: (value: string) => value && value !== '',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required(i18n.t('validation:required')),
    }),
  hospitalName: string().default('').optional(),
});

type FormValues = InferType<typeof validationSchema>;

const HealthClaimDetailsEditModal = ({ isOpen, healthClaim, onClose, onSuccess }: HealthClaimDetailsEditModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const {
    isUpdatingHealthClaim,
    serverErrors,
    diagnosisCodeOptions = [],
    isDiagnosisCodeSearching,
  } = useSelector(selectHealthClaim);
  const [selectedDiagnosisCodes, setSelectedDiagnosisCodes] = useState<DiagnosisCode[]>([]);
  const {
    id,
    claimNumber,
    accidentHappenedAt,
    dischargedAt,
    hospitalLengthOfStay,
    symptomsDescription,
    diagnosisDescription,
    businessPartner,
    hospitalName,
  } = healthClaim;

  useEffect(() => {
    if (isOpen) {
      setSelectedDiagnosisCodes(healthClaim.diagnosisCodes || []);
    }
  }, [isOpen, healthClaim.diagnosisCodes]);

  const handleDiagnosisCodeSearch = useCallback(
    (query: string) => {
      dispatch(diagnosisCodeSearch({ search: query }));
    },
    [dispatch],
  );

  const initialValues: FormValues = mergeInitialValues(validationSchema.getDefault(), {
    accidentHappenedAt: formatDateTime(accidentHappenedAt, DATETIME_FORMAT.INPUT),
    dischargedAt: formatDateTime(dischargedAt, DATETIME_FORMAT.INPUT),
    hospitalLengthOfStay,
    symptomsDescription,
    diagnosisDescription,
    businessPartnerId: businessPartner?.id || '',
    hospitalName,
  });

  const handleSubmit = (values: FormValues) => {
    const payload: HealthClaimUpdatePayload = {
      id: id,
      ...values,
      diagnosisCodeIds: selectedDiagnosisCodes.map((dc) => dc.id),
      businessPartnerId: Number(values.businessPartnerId),
    };
    dispatch(healthClaimUpdate(payload));
  };

  useEffect(() => {
    if (formikRef.current?.isSubmitting && !isUpdatingHealthClaim && isEmpty(serverErrors)) {
      formikRef.current?.setSubmitting(false);
      onSuccess && onSuccess();
    }

    if (formikRef.current?.isSubmitting && !isUpdatingHealthClaim && !isEmpty(serverErrors)) {
      formikRef.current?.setSubmitting(false);
    }
  }, [isUpdatingHealthClaim, serverErrors, onSuccess, formikRef]);

  return (
    <Modal size="lg" show={isOpen} onHide={onClose} centered>
      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, isSubmitting, setFieldValue, errors }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title>{t('claimEditModal.title', { claimNumber })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormGroupControl
                type="datetime-local"
                name="accidentHappenedAt"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.accidentHappenedAt.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                type="datetime-local"
                name="dischargedAt"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.dischargeDate.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                name="hospitalLengthOfStay"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.hospitalLengthOfStay.label')}
                required
                groupProps={groupProps}
              />
              <FormGroupControl
                as="textarea"
                name="symptomsDescription"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.symptomsDescription.label')}
                groupProps={groupProps}
                required
              />
              <FormGroupControl
                as="textarea"
                name="diagnosisDescription"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.diagnosisDescription.label')}
                groupProps={groupProps}
                required
              />
              <Form.Group className="mb-3">
                <Form.Label>{t('healthClaimDetails.diagnosisCodes')}</Form.Label>
                <AsyncTypeahead
                  id="diagnosis-code-typeahead"
                  multiple
                  isLoading={isDiagnosisCodeSearching}
                  onSearch={handleDiagnosisCodeSearch}
                  options={diagnosisCodeOptions}
                  selected={selectedDiagnosisCodes}
                  onChange={(selected) => setSelectedDiagnosisCodes(selected as DiagnosisCode[])}
                  labelKey={(option) => `${(option as DiagnosisCode).code} - ${(option as DiagnosisCode).description}`}
                  minLength={2}
                  placeholder={t('healthClaimDetails.diagnosisCodes')}
                />
              </Form.Group>
              <FormGroupTypeahead
                required
                name="businessPartnerId"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.panelHospital.label')}
                placeholder="Type or select"
                labelKey={`name`}
                groupProps={groupProps}
                options={healthClaim?.benefitTemplate?.panelPartners ?? []}
                defaultSelectedOptions={[healthClaim?.businessPartner?.name ?? '']}
                onChange={(selected) => {
                  (async () => {
                    await setFieldValue('businessPartnerId', (selected[0] as BusinessPartner | undefined)?.id ?? '');
                  })();
                }}
              />
              <FormGroupControl
                name="hospitalName"
                label={t('healthInsuranceClaimForm.injuryOrIllnessSection.hospitalName.label')}
                groupProps={groupProps}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? t('common:saving') : t('common:save')}
              </Button>
            </Modal.Footer>

            <ServerError errors={serverErrors} />
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export { HealthClaimDetailsEditModal };
